/**
 * Thumbs up/down on the QUESTION, for teachers and admins.
 *
 * Rates the question as an authoring asset — wrong answer key, ambiguous
 * wording, a diagram that does not match the text — not any one student's
 * attempt at it, and not a hint. Those have their own controls.
 *
 * A thumbs-DOWN opens an item in User Feedback for someone to look at, so it
 * asks for a reason once the rating is in. The reason is optional: a flag with
 * no words still files, because demanding an explanation is how flags stop
 * being left at all. A thumbs-up is recorded and asks for nothing.
 */
import { useState } from 'react';
import { ThumbsRating, type ThumbsValue } from '../components';
import './QuestionFeedbackControl.css';

export interface QuestionFeedbackEntry {
    rating: ThumbsValue;
    comment?: string;
    userId: string;
    role: 'student' | 'teacher' | 'admin';
    submittedAt: string;
}

export function QuestionFeedbackControl({ questionId, userFeedback, currentUserId, onRate }: {
    questionId: string;
    userFeedback?: QuestionFeedbackEntry[];
    /** Whose rating to show as selected. Absent → nothing preselected. */
    currentUserId?: string;
    onRate: (questionId: string, rating: ThumbsValue, comment?: string) => Promise<void>;
}) {
    const mine = currentUserId
        ? (userFeedback ?? []).find(f => f.userId === currentUserId)
        : undefined;
    const [askComment, setAskComment] = useState(false);
    const [comment, setComment] = useState('');
    const [saving, setSaving] = useState(false);

    // Everyone else's verdict, which is the useful context: one flag is an
    // opinion, four is a fact about the question.
    const others = (userFeedback ?? []).filter(f => f.userId !== currentUserId);
    const down = others.filter(f => f.rating === 'negative').length;
    const up = others.filter(f => f.rating === 'positive').length;

    return (
        <span className="qfb">
            <ThumbsRating
                size="sm"
                initialRating={mine?.rating ?? null}
                positiveTitle="This question is good"
                negativeTitle="Something is wrong with this question"
                onRate={r => onRate(questionId, r)}
                onAfterRate={r => { if (r === 'negative') { setComment(mine?.comment ?? ''); setAskComment(true); } }}
            />
            {(up > 0 || down > 0) && (
                <span className="qfb-others" title="Ratings from other reviewers">
                    {up > 0 && <span>{up} 👍</span>}
                    {down > 0 && <span className="qfb-down">{down} 👎</span>}
                </span>
            )}
            {askComment && (
                <span className="qfb-comment">
                    <input
                        autoFocus
                        placeholder="What's wrong with it? (optional)"
                        value={comment}
                        onChange={e => setComment(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Escape') setAskComment(false); }}
                    />
                    <button
                        type="button" className="btn-small" disabled={saving}
                        onClick={async () => {
                            setSaving(true);
                            // The rating is already saved; this only adds words
                            // to it, so a failure here loses nothing.
                            try { await onRate(questionId, 'negative', comment.trim() || undefined); }
                            finally { setSaving(false); setAskComment(false); }
                        }}
                    >{saving ? 'Saving…' : 'Add'}</button>
                    <button type="button" className="btn-small" onClick={() => setAskComment(false)}>Skip</button>
                </span>
            )}
        </span>
    );
}
